import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Download } from 'lucide-react';
import type { AxiosHeaders, AxiosResponse, RawAxiosResponseHeaders } from 'axios';
import { useSearchParams } from 'react-router-dom';

import { Button } from 'components/ui/button';
import { Card, CardContent } from 'components/ui/card';
import { PageLoadingState } from 'components/layout/PageLoadingState';
import { useToast } from 'components/ui/use-toast';
import { organizationAdmin } from 'services/api';
import type { OrganizationAdminScope } from 'services/api/organizationAdminClient';
import type {
  CourseProgressData,
  FlowAssignment,
  OrganizationAdminUserDetailData,
} from 'types';

import { OrgCourseProgress } from './course-progress';
import { OrganizationAdminHeader } from './organization-admin-header';
import { OrganizationSummary } from './organization-summary';
import { useOrganizationAdminOverview } from './use-organization-admin-overview';
import { UserDetailSection } from './user-detail';
import { UserProgressTable } from './user-progress-table';

const getFilenameFromContentDisposition = (header?: string | null) => {
  if (!header) {
    return null;
  }
  const utf8Match = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1]);
  }
  const quotedMatch = header.match(/filename="([^"]+)"/i);
  if (quotedMatch?.[1]) {
    return quotedMatch[1];
  }
  const unquotedMatch = header.match(/filename=([^;]+)/i);
  if (unquotedMatch?.[1]) {
    return unquotedMatch[1].trim();
  }
  return null;
};

const getContentDispositionHeader = (
  headers?: RawAxiosResponseHeaders | AxiosHeaders,
) => {
  if (!headers) {
    return null;
  }
  if (typeof (headers as AxiosHeaders).get === 'function') {
    const headerValue = (headers as AxiosHeaders).get('content-disposition');
    return typeof headerValue === 'string' ? headerValue : null;
  }
  const headerValue = (headers as RawAxiosResponseHeaders)['content-disposition'];
  if (Array.isArray(headerValue)) {
    return headerValue.join('; ');
  }
  return typeof headerValue === 'string' ? headerValue : null;
};

const sanitizeFilenamePart = (value: string) =>
  Array.from(value)
    .filter((character) => character.charCodeAt(0) >= 32)
    .join('')
    .replace(/[<>:"/\\|?*]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const buildExportFilename = (...parts: string[]) =>
  parts
    .map(sanitizeFilenamePart)
    .filter(Boolean)
    .join(' - ');

const getExportDate = () => new Date().toISOString().slice(0, 10);

const triggerBlobDownload = (
  response: AxiosResponse<Blob>,
  fallbackFilename: string,
) => {
  const blob = response.data instanceof Blob ? response.data : new Blob([response.data], { type: 'text/csv' });
  const filename = getFilenameFromContentDisposition(getContentDispositionHeader(response.headers)) || fallbackFilename;
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const getCourseStatus = (progress?: CourseProgressData): 'not_started' | 'in_progress' | 'completed' => {
  if (!progress) {
    return 'not_started';
  }
  if (progress.status === 'completed') {
    return 'completed';
  }
  const hasStarted = (progress.flows ?? []).some((flow) => flow.has_started || flow.completed_at);
  return hasStarted ? 'in_progress' : 'not_started';
};

const getFlowState = ({
  completed,
  locked,
  started,
}: {
  completed: boolean;
  locked: boolean;
  started: boolean;
}): 'completed' | 'in_progress' | 'locked' | 'not_started' => {
  if (completed) {
    return 'completed';
  }
  if (locked) {
    return 'locked';
  }
  if (started) {
    return 'in_progress';
  }
  return 'not_started';
};

const mapStandaloneFlow = (flow: FlowAssignment) => ({
  id: String(flow.id),
  name: flow.flow.name,
  currentState: getFlowState({
    completed: flow.is_completed,
    locked: false,
    started: Boolean(flow.has_started || flow.is_completed),
  }),
  completed: flow.is_completed,
  started: Boolean(flow.has_started || flow.is_completed),
  locked: false,
  progressPercent: flow.progress_percent ?? 0,
  completedAt: flow.last_completed_at ?? null,
  lastAccessedAt: flow.last_accessed_at ?? null,
});

const OrganizationAdmin: React.FC = () => {
  const { addToast } = useToast();
  const [searchParams] = useSearchParams();
  const targetOrganizationId = searchParams.get('organization_id')?.trim() || null;
  const organizationAdminScope = useMemo<OrganizationAdminScope | undefined>(
    () => (targetOrganizationId ? { organizationId: targetOrganizationId } : undefined),
    [targetOrganizationId],
  );
  const {
    overview,
    isLoading: overviewLoading,
    error: overviewError,
  } = useOrganizationAdminOverview(
    'Unable to load organization progress right now.',
    organizationAdminScope,
  );
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [selectedUserDetail, setSelectedUserDetail] = useState<OrganizationAdminUserDetailData | null>(null);
  const [selectedUserLoading, setSelectedUserLoading] = useState(false);
  const [selectedUserError, setSelectedUserError] = useState(false);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [combinedExportLoading, setCombinedExportLoading] = useState(false);
  const [aggregateExportLoading, setAggregateExportLoading] = useState(false);
  const [userProgressExportLoading, setUserProgressExportLoading] = useState(false);
  const [detailExportLoading, setDetailExportLoading] = useState(false);
  const [dailyScoresExportLoading, setDailyScoresExportLoading] = useState(false);
  const exportDate = getExportDate();

  useEffect(() => {
    setSelectedUserId(null);
    setSelectedUserDetail(null);
    setSelectedUserLoading(false);
    setSelectedUserError(false);
    setUserSearchQuery('');
  }, [targetOrganizationId]);

  useEffect(() => {
    if (selectedUserId === null) {
      setSelectedUserDetail(null);
      setSelectedUserLoading(false);
      setSelectedUserError(false);
      return;
    }

    let isMounted = true;

    const loadUserDetail = async () => {
      try {
        setSelectedUserLoading(true);
        setSelectedUserError(false);
        const data = organizationAdminScope
          ? await organizationAdmin.getUserDetail(selectedUserId, organizationAdminScope)
          : await organizationAdmin.getUserDetail(selectedUserId);
        if (isMounted) {
          setSelectedUserDetail(data);
        }
      } catch (error) {
        console.error('Failed to load organization user detail:', error);
        if (isMounted) {
          setSelectedUserError(true);
        }
      } finally {
        if (isMounted) {
          setSelectedUserLoading(false);
        }
      }
    };

    loadUserDetail();

    return () => {
      isMounted = false;
    };
  }, [organizationAdminScope, selectedUserId]);

  useEffect(() => {
    if (!overview || selectedUserId === null) {
      return;
    }
    const stillPresent = overview.users.some((user) => user.id === selectedUserId);
    if (!stillPresent) {
      setSelectedUserId(null);
      setSelectedUserDetail(null);
    }
  }, [overview, selectedUserId]);

  if (overviewLoading) {
    return <PageLoadingState />;
  }

  if (overviewError) {
    return (
      <main className="min-h-screen bg-background">
        <div className="py-8">
          <header className="mb-8">
            <h1 className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
              My Organization
            </h1>
            <p className="mt-2 text-muted-foreground">
              Review organization learning progress and user-level assigned content.
            </p>
          </header>
          <Card disableAnimation maxWidth="none" className="bg-white p-0 shadow-elevation-sm">
            <CardContent variant="custom" className="px-6 py-12 text-center">
              <div className="mx-auto mb-4 flex size-12 items-center justify-center rounded-full bg-destructive/10 text-destructive">
                <AlertTriangle className="size-5" />
              </div>
              <h2 className="text-base font-semibold text-foreground">Unable to load organization progress</h2>
              <p className="mt-2 text-sm text-muted-foreground">{overviewError}</p>
            </CardContent>
          </Card>
        </div>
      </main>
    );
  }

  const organization = overview?.organization ?? null;
  if (!overview || !organization) {
    return (
      <main className="min-h-screen bg-background">
        <div className="py-8">
          <header className="mb-8">
            <h1 className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
              My Organization
            </h1>
            <p className="mt-2 text-muted-foreground">
              Review organization learning progress and user-level assigned content.
            </p>
          </header>
          <Card disableAnimation maxWidth="none" className="bg-white p-0 shadow-elevation-sm">
            <CardContent variant="custom" className="px-6 py-16 text-center">
              <div className="mx-auto mb-4 flex size-12 items-center justify-center rounded-full bg-destructive/10 text-destructive">
                <AlertTriangle className="size-5" />
              </div>
              <h2 className="text-base font-semibold text-foreground">No organization available</h2>
              <p className="mt-2 text-sm text-muted-foreground">
                This page is only available when your account is attached to an organization.
              </p>
            </CardContent>
          </Card>
        </div>
      </main>
    );
  }

  const aggregateCourses = overview.org_default_courses.map((course) => {
    const aggregate = overview.aggregate_courses.find((item) => item.course_id === course.id);
    return {
      id: course.id,
      name: course.name,
      description: course.description,
      aggregateProgressPercent: aggregate?.progress_percent ?? 0,
      assignedUserCount: aggregate?.assigned_user_count ?? 0,
      notStartedCount: aggregate?.not_started_count ?? 0,
      inProgressCount: aggregate?.in_progress_count ?? 0,
      completedCount: aggregate?.completed_count ?? 0,
      lockedCount: aggregate?.locked_count,
    };
  });

  const tableUsers = overview.users.map((user) => ({
    id: user.id,
    displayName: user.display_name,
    email: user.email,
    courseProgress: overview.org_default_courses.map((course) => {
      const summary = user.course_progress[String(course.id)];
      return {
        courseId: course.id,
        assigned: Boolean(summary?.is_assigned),
        status: (summary?.status || 'not_assigned') as 'not_assigned' | 'not_started' | 'in_progress' | 'completed',
        progressPercent: summary?.progress_percent ?? 0,
        eligibility: summary?.eligibility,
      };
    }),
  }));
  const normalizedUserSearch = userSearchQuery.trim().toLowerCase();
  const filteredTableUsers = tableUsers.filter((user) => {
    if (!normalizedUserSearch) {
      return true;
    }
    return (
      user.displayName.toLowerCase().includes(normalizedUserSearch)
      || user.email.toLowerCase().includes(normalizedUserSearch)
    );
  });

  const detailShape = selectedUserDetail ? {
    displayName: selectedUserDetail.user.display_name,
    email: selectedUserDetail.user.email,
    avatarUrl: selectedUserDetail.user.avatar_url ?? null,
    dailyScoresReport: selectedUserDetail.daily_scores_report,
    assignedCourses: selectedUserDetail.assigned_courses.map((course) => {
      const progress = selectedUserDetail.all_course_progress[course.id];
      return {
        id: String(course.id),
        name: course.name,
        status: getCourseStatus(progress),
        eligibility: progress?.eligibility ?? course.eligibility,
        progressPercent: progress?.progress_percent ?? 0,
        completedAt: progress?.completed_at ?? null,
        lastAccessedAt: progress?.last_accessed_at ?? null,
        flows: (progress?.flows ?? []).map((flow) => ({
          id: String(flow.id),
          name: flow.flow_details?.name || 'Untitled module',
          chapterId: flow.chapter_id,
          chapterTitle: flow.chapter_title,
          currentState: getFlowState({
            completed: Boolean(flow.completed_at),
            locked: Boolean(flow.is_locked),
            started: Boolean(flow.has_started || flow.is_current || flow.completed_at),
          }),
          completed: Boolean(flow.completed_at),
          started: Boolean(flow.has_started || flow.is_current || flow.completed_at),
          locked: Boolean(flow.is_locked),
          progressPercent: flow.completed_at ? 100 : (flow.has_started ? 50 : 0),
          completedAt: flow.completed_at ?? null,
          lastAccessedAt: flow.last_accessed_at ?? null,
        })),
      };
    }),
    standaloneFlows: selectedUserDetail.flow_assignments.map(mapStandaloneFlow),
  } : null;

  const handleAggregateExport = async () => {
    try {
      setAggregateExportLoading(true);
      const response = organizationAdminScope
        ? await organizationAdmin.exportAggregateProgress(organizationAdminScope)
        : await organizationAdmin.exportAggregateProgress();
      triggerBlobDownload(
        response,
        `${buildExportFilename(organization.name, 'Aggregate Progress', exportDate)}.csv`,
      );
      addToast({
        title: 'Export ready',
        description: 'Aggregate progress CSV download has started.',
      });
    } catch (error: any) {
      console.error('Failed to export aggregate progress:', error);
      addToast({
        title: 'Export failed',
        description: error?.response?.data?.detail || error?.message || 'Unable to export aggregate progress.',
        variant: 'destructive',
      });
    } finally {
      setAggregateExportLoading(false);
    }
  };

  const handleCombinedExport = async () => {
    try {
      setCombinedExportLoading(true);
      const response = organizationAdminScope
        ? await organizationAdmin.exportCombined(selectedUserId ?? undefined, organizationAdminScope)
        : await organizationAdmin.exportCombined(selectedUserId ?? undefined);
      triggerBlobDownload(
        response,
        `${buildExportFilename(organization.name, 'All Exports', exportDate)}.zip`,
      );
      addToast({
        title: 'Export ready',
        description: 'Organization report download has started.',
      });
    } catch (error: any) {
      console.error('Failed to export combined ZIP:', error);
      addToast({
        title: 'Export failed',
        description: error?.response?.data?.detail || error?.message || 'Unable to export organization report.',
        variant: 'destructive',
      });
    } finally {
      setCombinedExportLoading(false);
    }
  };

  const handleUserProgressExport = async () => {
    try {
      setUserProgressExportLoading(true);
      const response = organizationAdminScope
        ? await organizationAdmin.exportUserProgress(userSearchQuery, organizationAdminScope)
        : await organizationAdmin.exportUserProgress(userSearchQuery);
      triggerBlobDownload(
        response,
        `${buildExportFilename(organization.name, 'User Progress', exportDate)}.csv`,
      );
      addToast({
        title: 'Export ready',
        description: 'User progress CSV download has started.',
      });
    } catch (error: any) {
      console.error('Failed to export user progress:', error);
      addToast({
        title: 'Export failed',
        description: error?.response?.data?.detail || error?.message || 'Unable to export user progress.',
        variant: 'destructive',
      });
    } finally {
      setUserProgressExportLoading(false);
    }
  };

  const handleDailyScoresExport = async () => {
    if (selectedUserId === null) {
      return;
    }
    try {
      setDailyScoresExportLoading(true);
      const response = organizationAdminScope
        ? await organizationAdmin.exportUserDailyScores(selectedUserId, organizationAdminScope)
        : await organizationAdmin.exportUserDailyScores(selectedUserId);
      const userName = selectedUserDetail?.user.display_name || 'Selected User';
      triggerBlobDownload(
        response,
        `${buildExportFilename(organization.name, userName, 'Daily Scores', exportDate)}.csv`,
      );
      addToast({
        title: 'Export ready',
        description: 'Daily scores CSV download has started.',
      });
    } catch (error: any) {
      console.error('Failed to export daily scores:', error);
      addToast({
        title: 'Export failed',
        description: error?.response?.data?.detail || error?.message || 'Unable to export daily scores.',
        variant: 'destructive',
      });
    } finally {
      setDailyScoresExportLoading(false);
    }
  };

  const handleDetailExport = async () => {
    if (selectedUserId === null) {
      return;
    }
    try {
      setDetailExportLoading(true);
      const response = organizationAdminScope
        ? await organizationAdmin.exportUserDetailProgress(selectedUserId, organizationAdminScope)
        : await organizationAdmin.exportUserDetailProgress(selectedUserId);
      const userName = selectedUserDetail?.user.display_name || 'Selected User';
      triggerBlobDownload(
        response,
        `${buildExportFilename(organization.name, userName, 'User Detail Report', exportDate)}.csv`,
      );
      addToast({
        title: 'Export ready',
        description: 'Selected user detail CSV download has started.',
      });
    } catch (error: any) {
      console.error('Failed to export selected user detail progress:', error);
      addToast({
        title: 'Export failed',
        description: error?.response?.data?.detail || error?.message || 'Unable to export selected user detail progress.',
        variant: 'destructive',
      });
    } finally {
      setDetailExportLoading(false);
    }
  };

  return (
    <main className="min-h-screen">
      <div className="space-y-8 py-8">
        <OrganizationAdminHeader activeTab="progress" organizationName={organization.name} />

        <section className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-3xl font-semibold text-foreground">Progress Data</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Export progress reports for the full organization, or drill into user-level data below.
            </p>
          </div>
          <Button
            type="button"
            size="auto"
            data-testid="org-admin-export-combined"
            onClick={handleCombinedExport}
            disabled={combinedExportLoading}
          >
            <Download className="size-4" />
            {combinedExportLoading ? 'Preparing ZIP...' : 'Download All Exports'}
          </Button>
        </section>

        <OrganizationSummary
          activeUserCount={overview.summary.active_user_count}
          defaultCourseCount={overview.summary.org_default_course_count}
          averageProgressPercent={overview.summary.average_progress_percent}
          usersInProgressCount={overview.summary.users_in_progress_count}
          usersFullyCompleteCount={overview.summary.users_fully_complete_count}
          usersActiveLast7DaysCount={overview.summary.users_active_last_7_days_count}
        />

        <OrgCourseProgress
          courses={aggregateCourses}
          onExport={handleAggregateExport}
          isExporting={aggregateExportLoading}
        />

        <UserProgressTable
          users={filteredTableUsers}
          courses={overview.org_default_courses.map((course) => ({
            id: course.id,
            name: course.name,
          }))}
          selectedUserId={selectedUserId || undefined}
          onSelectUser={setSelectedUserId}
          searchQuery={userSearchQuery}
          onSearchQueryChange={setUserSearchQuery}
          onExport={handleUserProgressExport}
          isExporting={userProgressExportLoading}
        />

        <UserDetailSection
          userDetail={detailShape}
          isLoading={selectedUserLoading}
          isError={selectedUserError}
          onExportDetail={handleDetailExport}
          isExportingDetail={detailExportLoading}
          onExportDailyScores={handleDailyScoresExport}
          isExportingDailyScores={dailyScoresExportLoading}
        />
      </div>
    </main>
  );
};

export default OrganizationAdmin;
